import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { FileText, Send } from "lucide-react";
import { useTranslation } from "@/packages/utils";
import type { ChatPanelInputProps } from "./types";
import { Button } from "../primitives";

export function ChatPanelInput({
  placeholder,
  onSend,
  context,
}: ChatPanelInputProps) {
  const { t } = useTranslation(["chat"]);
  const resolvedPlaceholder = placeholder ?? t("chat:input.placeholder");
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const trimmed = value.trim();

  function handleSend() {
    if (!trimmed) return;
    onSend(trimmed);
    setValue("");
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  const ContextIcon = context?.icon ?? FileText;

  return (
    <div className="px-4 pt-2 pb-px outline-none focus:outline-none ring-0 focus:ring-0 relative top-2 py-0 border-t border-border space-y-2">
      {context && (
        <div className="flex items-center gap-1.5 border-[1px] border-accent/50 max-w-min rounded-md py-1 px-2">
          <ContextIcon size={12} className="text-muted shrink-0" />
          <span className="text-[11px] text-muted truncate">
            {context.label}
          </span>
        </div>
      )}
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={resolvedPlaceholder}
          className="flex-1 bg-card rounded-lg px-4 py-2.5 text-sm text-primary placeholder:text-muted outline-none focus:ring-1 focus:ring-transparent focus:ring-offset-0 focus:ring-offset-background"
        />
        <Button
          variant="primary"
          icon={Send}
          iconOnly
          onClick={handleSend}
          disabled={!trimmed}
          className="h-9 w-9 rounded-lg shrink-0"
          aria-label="Send message"
        />
      </div>
    </div>
  );
}
